import axios from 'axios';
import config from '../config';

let cachedSnapshot = null;
let lastCheckTime = null;
const CACHE_DURATION = 5000; // Cache for 5 seconds

/**
 * @typedef {{ deviceOffline: boolean, backendUnreachable: boolean }} ConnectivitySnapshot
 */

/**
 * Single health probe: device offline vs backend not reachable (browser online but no HTTP response).
 * @returns {Promise<ConnectivitySnapshot>}
 */
async function probeConnectivity() {
  const now = Date.now();
  if (cachedSnapshot && lastCheckTime && (now - lastCheckTime) < CACHE_DURATION) {
    return cachedSnapshot;
  }

  const t = Date.now();

  if (!navigator.onLine) {
    cachedSnapshot = { deviceOffline: true, backendUnreachable: false };
    lastCheckTime = t;
    return cachedSnapshot;
  }

  try {
    await axios.get(`${config.API_BASE_URL}/api/health/`, { timeout: 3000 });
    cachedSnapshot = { deviceOffline: false, backendUnreachable: false };
    lastCheckTime = t;
    return cachedSnapshot;
  } catch (error) {
    lastCheckTime = t;

    if (error.response && typeof error.response.status === 'number') {
      cachedSnapshot = { deviceOffline: false, backendUnreachable: false };
      return cachedSnapshot;
    }

    if (!navigator.onLine) {
      cachedSnapshot = { deviceOffline: true, backendUnreachable: false };
      return cachedSnapshot;
    }

    cachedSnapshot = { deviceOffline: false, backendUnreachable: true };
    return cachedSnapshot;
  }
}

/**
 * @returns {Promise<ConnectivitySnapshot>}
 */
export const getConnectivitySnapshot = () => probeConnectivity();

/**
 * Legacy: true only when the device should be treated as offline (no network / browser reports offline).
 * @returns {Promise<boolean>}
 */
export const checkApiHealth = async () => {
  const s = await probeConnectivity();
  return s.deviceOffline;
};

/**
 * @returns {Promise<boolean>} true if the device should be treated as offline for UI (banner, etc.)
 */
export const isOffline = async () => {
  const s = await probeConnectivity();
  return s.deviceOffline;
};

/**
 * Clear cached connectivity (e.g. after browser online event)
 */
export const clearOfflineCache = () => {
  cachedSnapshot = null;
  lastCheckTime = null;
};
